import { useSWRConfig } from 'swr';

import shopApi from '@services/shop';

import { useShop } from './useShop';
import { activateItemOptimistically, deactivateItemOptimistically, dismissHintOptimistically } from './useInvalidateLocalPotential';

/**
 * Writes for the two Local Potential surfaces that change shop-row state, both
 * via the existing `updateMyShop` → `update-my-shop` path (the same write
 * `onboardingComplete` uses — no bespoke endpoints):
 *
 *   - claimWin / unclaimWin — the four self-attested wins (`localActivations`).
 *   - dismiss / restore — hide / restore a suggestion or quick win
 *     (`dismissedHints`, namespaced `suggestion:<id>` / `quickwin:<id>`).
 *
 * Each read-modify-writes the relevant map (so a partial update never clobbers a
 * sibling key), optimistically mutates the `['local-potential', shopId]` cache so
 * the UI doesn't blink, then revalidates the score and the shop.
 */
export function useLocalPotentialActions() {
	const { mutate } = useSWRConfig();
	const { shop, mutate: mutateShop } = useShop();
	const shopId = shop?.id;
	const key = ['local-potential', shopId];

	const settle = async () => {
		await Promise.all([mutateShop?.(), mutate(key)]);
	};

	const claimWin = async (winKey) => {
		const localActivations = { ...(shop?.localActivations || {}), [winKey]: true };
		await mutate(key, (current) => activateItemOptimistically(current, winKey), { revalidate: false });
		await shopApi.updateMyShop(shopId, { localActivations });
		await settle();
	};

	const unclaimWin = async (winKey) => {
		const localActivations = { ...(shop?.localActivations || {}), [winKey]: false };
		await mutate(key, (current) => deactivateItemOptimistically(current, winKey), { revalidate: false });
		await shopApi.updateMyShop(shopId, { localActivations });
		await settle();
	};

	const dismiss = async (hintId) => {
		const dismissedHints = [...new Set([...(shop?.dismissedHints || []), hintId])];
		await mutate(key, (current) => dismissHintOptimistically(current, hintId), { revalidate: false });
		await shopApi.updateMyShop(shopId, { dismissedHints });
		await settle();
	};

	const restore = async (hintId) => {
		const dismissedHints = (shop?.dismissedHints || []).filter((id) => id !== hintId);
		await shopApi.updateMyShop(shopId, { dismissedHints });
		await settle();
	};

	return { claimWin, unclaimWin, dismiss, restore };
}
